import { MappedDevice } from "@pages/Assets/Devices/ListView/colDefs";
import { ColDefWithTab } from "@pages/Assets/common/FilterColumn/context";
import _ from "lodash";
import type { Data } from "./types";
import { MenuItems } from "./CSVMenu";

export const mapToCSVData = <T, O>(
  list: T[],
  columns: ColDefWithTab<T, O>[],
  filename: string,
  options: O
): Data => {
  const data = list.map((r) =>
    columns.reduce((acc: any, c) => {
      let value;
      if (c.csvValue !== undefined) {
        value = c.csvValue(r, options);
      } else {
        value = r[c.key];
      }

      if (typeof value === "string" && value) {
        value = value.replace(/(\r\n|\r|\n|\t)/gm, " ").replace(/"/g, '""');
      }

      acc[c.key] = value;
      return acc;
    }, {})
  );
  const headers = columns.map((c) => ({
    label: c.csvHeader ?? c.label,
    key: c.key.toString(),
  }));

  return { data, headers, filename };
};

export const mapDeviceToCSVData = (
  list: MappedDevice[],
  columns: ColDefWithTab<MappedDevice>[],
  filename: string,
  item: MenuItems
): Data => {
  const data = mapToCSVData(list, columns, filename, undefined);
  if (item === MenuItems.ExtendedProperties) {
    const extendedPropertiesHeaders = _.orderBy(
      _.uniq(
        _.flatten(
          list
            .filter((l) => l.provisioned)
            .map((l) =>
              _.flatten(l.extendedProperties?.map((e) => Object.keys(e)) ?? [])
            )
        )
      )
    ).map((h) => ({ key: `extended.${h}`, label: `Extended Property:${h}` }));

    const snmpHeaders = _.orderBy(
      _.uniq(
        _.flatten(
          list
            .filter((l) => l.provisioned)
            .map(
              (l) =>
                l.snmpProperties
                  ?.filter((s) => s.name)
                  .map((s) => s.name ?? "") ?? []
            )
        )
      )
    ).map((h) => ({
      label: `SNMP:${h}`,
      key: `snmp.${h}`,
    }));

    const mappedExtendedProperties = list
      .map((l) => l.extendedProperties)
      .map(
        (a) =>
          a?.map((e) => ({
            ...Object.keys(e ?? {}).reduce<object>(
              (acc, value) => ({
                ...acc,
                [`extended.${value}`]: _.get(e, value),
              }),
              {}
            ),
          })) ?? []
      )
      .map((a) => _.merge({}, ...a));

    const mappedSNMPProperties = list.map((l) =>
      l.snmpProperties?.reduce<object>(
        (acc, s) => ({ ...acc, [`snmp.${s.name ?? ""}`]: s.value }),
        {}
      )
    );

    const allHeaders = _.concat(
      data.headers as [],
      extendedPropertiesHeaders,
      snmpHeaders
    );
    const allData = _.zip(
      data.data as unknown as string[],
      mappedExtendedProperties,
      mappedSNMPProperties
    ).map((value) => _.merge({}, value[0], value[1], value[2]));

    return { ...data, data: allData, headers: allHeaders };
  }

  return data;
};
